import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Star } from "@phosphor-icons/react";
import { SERVICES, HERO_IMAGE } from "@/lib/api";

const stats = [
  { label: "Services", value: "07" },
  { label: "Open Roles", value: "11" },
  { label: "Plaza Hours", value: "24/7" },
  { label: "Member Tier", value: "PRO" },
];

export default function Home() {
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative min-h-[90vh] flex items-end overflow-hidden border-b border-black/10">
        <img
          src={HERO_IMAGE}
          alt="Twinstars Plaza"
          className="absolute inset-0 w-full h-full object-cover"
          loading="eager"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/40 to-transparent" />
        <div className="relative z-10 max-w-[1400px] mx-auto px-6 lg:px-10 pb-20 pt-32 w-full grid grid-cols-1 md:grid-cols-12 gap-10 text-white">
          <div className="md:col-span-8">
            <p className="label-eyebrow text-white/80 mb-6" data-testid="hero-eyebrow">
              ◆ Plaza · Lifestyle · Hospitality
            </p>
            <motion.h1
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="font-display text-5xl sm:text-6xl lg:text-[6.5rem] font-black tracking-tighter leading-[0.9]"
              data-testid="hero-headline"
            >
              Seven services.<br/>
              One landmark.<br/>
              <span className="text-[#FF3333]">Twinstars.</span>
            </motion.h1>
            <p className="mt-8 max-w-xl text-white/80 text-base leading-relaxed">
              From ringside seats to spa rituals, gym membership to guesthouse stays — book everything Twinstars Plaza offers, and apply for the people behind the magic.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Link to="/services" className="btn-sharp bg-white text-black border-white hover:bg-transparent hover:text-white" data-testid="hero-cta-services">
                Explore services <ArrowRight size={16} />
              </Link>
              <Link to="/jobs" className="btn-sharp bg-transparent text-white border-white hover:bg-white hover:text-black" data-testid="hero-cta-jobs">
                Apply for jobs
              </Link>
            </div>
          </div>
          <div className="md:col-span-4 md:border-l border-white/20 md:pl-10 self-end">
            <div className="grid grid-cols-2 gap-y-6">
              {stats.map((s) => (
                <div key={s.label}>
                  <p className="font-display text-4xl font-black">{s.value}</p>
                  <p className="label-eyebrow text-white/60 mt-1">{s.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* MARQUEE */}
      <section className="border-b border-black/10 py-5 bg-black text-white">
        <div className="marquee">
          <div className="marquee-track">
            {[...SERVICES, ...SERVICES].map((s, i) => (
              <span key={`${s.key}-${i}`} className="font-display font-black text-3xl tracking-tight px-6">
                {s.name.toUpperCase()} <span className="text-white/30 mx-2">/</span>
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* BENTO SERVICES */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24" data-testid="services-bento">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-16">
          <div>
            <p className="label-eyebrow text-black/60">01 / SERVICES</p>
            <h2 className="font-display text-4xl md:text-5xl font-black tracking-tighter leading-none mt-3">
              Each service —<br/>a destination of its own.
            </h2>
          </div>
          <Link to="/services" className="btn-outline" data-testid="bento-all-services">View all</Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
          {/* Boxing (large) */}
          <ServiceTile service={SERVICES[0]} className="md:col-span-8 md:row-span-2 aspect-[16/10] md:aspect-auto md:h-[520px]" big />
          {/* Spa */}
          <ServiceTile service={SERVICES[1]} className="md:col-span-4 h-[256px]" />
          {/* Gym */}
          <ServiceTile service={SERVICES[2]} className="md:col-span-4 h-[256px]" />
          {/* Guesthouse */}
          <ServiceTile service={SERVICES[3]} className="md:col-span-6 h-[300px]" />
          {/* Barber */}
          <ServiceTile service={SERVICES[4]} className="md:col-span-3 h-[300px]" />
          {/* Washing */}
          <ServiceTile service={SERVICES[5]} className="md:col-span-3 h-[300px]" />
          {/* Laundry */}
          <ServiceTile service={SERVICES[6]} className="md:col-span-12 h-[280px]" wide />
        </div>
      </section>

      {/* MANIFESTO */}
      <section className="bg-black text-white">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-28 grid grid-cols-1 md:grid-cols-12 gap-12 items-start">
          <div className="md:col-span-3">
            <p className="label-eyebrow text-white/40">02 / MANIFESTO</p>
          </div>
          <div className="md:col-span-9">
            <p className="font-display text-3xl md:text-5xl font-black tracking-tighter leading-[1.05]">
              We refuse the idea that hospitality should be scattered. Twinstars Plaza is one address for the seven things you do every week — ring, recover, train, sleep, sharpen, shine and clean.
            </p>
            <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-6 text-sm">
              <Pillar n="01" t="Book in seconds" d="Unified booking across every service." />
              <Pillar n="02" t="Mobile-money native" d="Pay the way Africa already pays." />
              <Pillar n="03" t="Member rewards" d="Wallet credits across the plaza." />
              <Pillar n="04" t="Talent in-house" d="Hire staff directly from our roster." />
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24">
        <div className="border border-black bg-white p-10 md:p-16 flex flex-col md:flex-row gap-8 items-start md:items-end justify-between">
          <div>
            <p className="label-eyebrow">03 / GET STARTED</p>
            <h3 className="font-display text-3xl md:text-5xl font-black tracking-tighter leading-none mt-3">
              Create your account.<br/>Book in under 60 seconds.
            </h3>
          </div>
          <div className="flex flex-wrap gap-3">
            <Link to="/register" className="btn-primary" data-testid="cta-register">Create account</Link>
            <Link to="/services" className="btn-outline">See services</Link>
          </div>
        </div>
      </section>
    </div>
  );
}

function Pillar({ n, t, d }) {
  return (
    <div className="border-t border-white/20 pt-4">
      <p className="font-mono text-xs text-white/40">{n}</p>
      <p className="font-display text-lg font-bold mt-1">{t}</p>
      <p className="text-white/60 text-sm mt-1">{d}</p>
    </div>
  );
}

function ServiceTile({ service, className = "", big = false, wide = false }) {
  return (
    <Link
      to={`/book/${service.key}`}
      data-testid={`service-tile-${service.key}`}
      className={`service-tile group relative block bg-black ${className}`}
    >
      <img
        src={service.image}
        alt={service.name}
        className="absolute inset-0 w-full h-full object-cover opacity-90 group-hover:opacity-70 transition-opacity duration-500"
        loading="lazy"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent" />
      <div className="relative z-10 h-full w-full p-6 md:p-8 flex flex-col justify-between text-white">
        <div className="flex items-start justify-between">
          <p className="font-mono text-xs tracking-widest text-white/70">{String(SERVICES.indexOf(service) + 1).padStart(2, "0")} / {service.key.toUpperCase()}</p>
          <span className="w-2 h-2" style={{ background: service.accent }} />
        </div>
        <div>
          <h3 className={`font-display font-black tracking-tighter leading-none ${big ? "text-5xl md:text-7xl" : wide ? "text-4xl md:text-5xl" : "text-2xl md:text-3xl"}`}>
            {service.name}
          </h3>
          <p className="mt-2 text-white/70 text-sm max-w-md">{service.tagline}</p>
          <div className="mt-5 inline-flex items-center gap-2 text-sm font-semibold opacity-80 group-hover:opacity-100 transition">
            Book now <ArrowRight size={16} className="group-hover:translate-x-1 transition" />
          </div>
        </div>
      </div>
    </Link>
  );
}
